import { effect, signal } from '@preact/signals';
import type { Place } from './astro/frame';
import { CULTURES, type CultureId } from './data/cultures';
import type { SimbadObject } from './live/simbad';

// ---------- Persistence (best effort; private windows may throw) ----------
/** Everything the app saves on the phone is kept under this prefix. */
export const STORE = 'ahillah:';
/** The text saved under `key`, or null. */
export function savedText(key: string): string | null {
  try {
    return localStorage.getItem(STORE + key);
  } catch {
    return null;
  }
}
export function loadJSON<T>(key: string, fallback: T): T {
  try {
    const s = savedText(key);
    const v = s ? JSON.parse(s) : null;
    // A saved null means nothing saved; saved objects gain any fields added since. Lists come back as saved
    // (spread into an object they would stop being lists), and a saved value of the wrong shape is ignored.
    if (v === null) return fallback;
    if (Array.isArray(fallback)) return (Array.isArray(v) ? v : fallback) as T;
    if (fallback && typeof fallback === 'object') return v && typeof v === 'object' && !Array.isArray(v) ? { ...fallback, ...v } : fallback;
    return v;
  } catch {
    return fallback;
  }
}
export function saveJSON(key: string, value: unknown) {
  try {
    localStorage.setItem(STORE + key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
// What comes back is untrusted: an older version or a damaged store can hold valid JSON of the wrong kind, and one
// bad number (a null latitude) once stopped the app starting. Each value is checked before it is used.
const within = (v: unknown, lo: number, hi: number): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
/** Whether the browser knows a time zone by this name: an unknown one makes every date in the app fail to format. */
export function isTimeZone(v: unknown): v is string {
  if (typeof v !== 'string' || !v || v.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: v });
    return true;
  } catch {
    return false;
  }
}

// ---------- Settings ----------
export interface Settings {
  constellationLines: boolean;
  constellationNames: boolean;
  constellationBounds: boolean;
  starNames: boolean;
  deepSky: boolean;
  milkyWay: boolean;
  grid: 'none' | 'altaz' | 'equatorial';
  ecliptic: boolean;
  satellites: 'off' | 'bright' | 'all';
  starlink: boolean;
  meteors: boolean;
  landscape: boolean;
  seeThroughGround: boolean;
  bortle: number;
  /** Estimate the sky's brightness from the location (light-pollution atlas). */
  bortleAuto: boolean;
  /** Estimated zenith brightness (mag/arcsec²) when estimated from the location. */
  sqm: number | null;
  starBoost: number; // 0 realistic … 2 rich
  /** "Naked eye": only what the eye can see tonight, named. Off is "Full sky", everything Ahillah knows: the default. */
  nakedEye: boolean;
  nightMode: boolean;
  /** Night vision's warmth, as the iPhone's Night Shift: 0 less warm (about 5000 K) to 1 more warm (about 1500 K). */
  nightWarmth: number;
  cameraFeed: boolean;
  cameraFov: number; // degrees across the shorter screen side
  /**
   * Camera help (src/live/camerahelp.ts): in pointing mode, the rear camera rings the bright stars and planets it finds
   * and notes cloud. Off unless the person switches it on; frames never leave the phone and are never kept.
   */
  cameraHelp: boolean;
  twinkle: boolean;
  /** Arabic: names on cards and in narration, the Arabian sky and the Moon's stations. Off unless chosen. */
  arabic: boolean;
  arabicNames: boolean;
  skyCulture: CultureId;
  /** Field-of-view circle at the centre of the view: none, binoculars or a small telescope. */
  eyepiece: 'none' | 'bino7' | 'bino10' | 'scope';
  /** In pointing mode, say the name of whatever the phone settles on. */
  sayPointing: boolean;
  /** On a phone, open following the phone (pointing mode) rather than by dragging. */
  startPointing: boolean;
  lunarStations: boolean;
  /** Photographs of the sky (Digitized Sky Survey) when zoomed in. */
  survey: boolean;
  alerts: boolean;
  /** Buzz with the in-app alert banner (Android; iPhones do not allow it). */
  alertVibrate: boolean;
  /** Notifications while closed: clear evenings at about 4 pm, bright ISS passes, and notable sky events. */
  pushEvening: boolean;
  pushIss: boolean;
  pushEvents: boolean;
  /** Saudi Arabia's month alerts (server/watch.ts): Ramaḍān, Shawwāl and Dhū al-Ḥijjah (on unless turned off), and
   *  "the new month begins tomorrow" for every other month (off unless chosen). */
  pushSought: boolean;
  pushMonths: boolean;
  /** The white days: on the afternoon of the 12th, that the 13th, 14th and 15th come next (BUILD-SPEC item 36). */
  pushWhiteDays: boolean;
  /** A sound with notifications (BUILD-SPEC item 18): silent unless chosen. */
  pushSound: boolean;
  /** The voice that reads the Arabic aloud (DECISIONS 107): a man's, or a woman's if chosen. */
  arabicVoice: 'm' | 'f';
}

// The sky starts clean: stars, the planets, the Moon, the Milky Way and the horizon. Everything else is
// switched on by the person (What to show), rather than off.
export const DEFAULT_SETTINGS: Settings = {
  constellationLines: false,
  constellationNames: false,
  constellationBounds: false,
  starNames: false,
  deepSky: false,
  milkyWay: true,
  grid: 'none',
  ecliptic: false,
  satellites: 'off',
  starlink: false,
  meteors: false,
  landscape: true,
  seeThroughGround: false,
  bortle: 7,
  bortleAuto: true,
  sqm: null,
  starBoost: 1,
  nakedEye: false,
  nightMode: false,
  nightWarmth: 0.5,
  cameraFeed: false,
  cameraFov: 52,
  cameraHelp: false,
  twinkle: true,
  arabic: true,
  arabicNames: false,
  skyCulture: 'modern',
  eyepiece: 'none',
  sayPointing: false,
  startPointing: true,
  lunarStations: false,
  survey: true,
  alerts: true,
  alertVibrate: false,
  pushEvening: true,
  pushIss: true,
  pushEvents: true,
  pushSought: true,
  pushMonths: false,
  pushWhiteDays: false,
  pushSound: false,
  arabicVoice: 'm',
};

type Check = (v: unknown) => boolean;
const oneOf = (...xs: unknown[]): Check => (v) => xs.includes(v);
type SwitchKey = { [K in keyof Settings]: Settings[K] extends boolean ? K : never }[keyof Settings];
/** What each setting that is not an on/off switch may be (a switch must be true or false). */
const SETTING_CHECKS: Record<Exclude<keyof Settings, SwitchKey>, Check> = {
  grid: oneOf('none', 'altaz', 'equatorial'),
  satellites: oneOf('off', 'bright', 'all'),
  bortle: (v) => Number.isInteger(v) && within(v, 1, 9),
  sqm: (v) => v === null || within(v, 10, 25),
  starBoost: (v) => within(v, 0, 2),
  nightWarmth: (v) => within(v, 0, 1),
  cameraFov: (v) => within(v, 30, 90),
  // A culture this version does not have (from an older or newer one) gives way to Modern.
  skyCulture: (v) => CULTURES.some((c) => c.id === v),
  eyepiece: oneOf('none', 'bino7', 'bino10', 'scope'),
  arabicVoice: oneOf('m', 'f'),
};
/**
 * Saved settings, checked one by one over `base`: each that is valid is kept, each that is missing, of the wrong
 * kind or out of range stays as `base` has it, and anything this version does not know is dropped.
 */
export function cleanSettings(v: unknown, base: Settings = DEFAULT_SETTINGS): Settings {
  const out = { ...base };
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out;
  const saved = v as Record<string, unknown>;
  for (const k of Object.keys(DEFAULT_SETTINGS) as (keyof Settings)[]) {
    const check: Check = (SETTING_CHECKS as Partial<Record<keyof Settings, Check>>)[k] ?? ((x) => typeof x === 'boolean');
    if (saved[k] !== undefined && check(saved[k])) (out as Record<string, unknown>)[k] = saved[k];
  }
  return out;
}

/** The sky's layers as they start: everything that is switched on rather than off. */
const CLEAN_SKY: Partial<Settings> = {
  constellationLines: false, constellationNames: false, constellationBounds: false, starNames: false, deepSky: false, grid: 'none',
  ecliptic: false, satellites: 'off', starlink: false, meteors: false, lunarStations: false, arabicNames: false, skyCulture: 'modern',
};
/** Ask what to show: once, for an install from before the sky started clean. */
export const whatToShow = signal(false);
const saved = (() => {
  try {
    return savedText('settings') !== null;
  } catch {
    return false;
  }
})();
/** Whether this install had settings before this start (not a first run). */
export const hadSettings = saved;
/** The settings as stored, for the moves from older versions below, which read settings no longer kept. */
const stored = loadJSON<Record<string, unknown>>('settings', {});
const loaded = cleanSettings(stored);
// 29 Sep 2026: the sky starts clean and Arabic became one setting, off unless chosen. An install from
// before moves over once (keeping Arabic if it had chosen the Arabian sky or the Moon's stations) and is
// asked what to show; a new install starts clean and chooses in the welcome.
if (!loadJSON<{ v?: number }>('cleanSky', {}).v) {
  if (saved) {
    const choseArabic = loaded.skyCulture === 'arabic' || loaded.lunarStations || loaded.arabicNames;
    Object.assign(loaded, CLEAN_SKY, { arabic: choseArabic });
    whatToShow.value = !!loadJSON<{ v?: number }>('onboarded', {}).v;
  }
  saveJSON('cleanSky', { v: 1 });
}
// 15 Oct 2026: Arabic names are on from the first run (BUILD-SPEC item 10); an install from before with them off is
// offered them once (src/ui/OnceOffer.tsx), and Sky view offers the Arabian star groups and the Moon's stations
// whether they are on or not (item 13).
// 30 Sep 2026: night vision became one warmth, like Night Shift, instead of a colour and a brightness. The
// colour chosen before becomes a warmth (red the warmest), and the old settings go (cleanSettings drops them).
if (stored.nightColour !== undefined && !stored.nightWarmth) loaded.nightWarmth = stored.nightColour === 'amber' ? 0.7 : stored.nightColour === 'orange' ? 0.85 : 1;
// 5 Oct 2026: Ahillah no longer shows planes. A saved `aircraft` switch goes like any other setting this version
// does not know (cleanSettings drops it), and the store is written again without it below.
export const settings = signal<Settings>(loaded);
effect(() => saveJSON('settings', settings.value));

// ---------- What is on the sky: any mix of kinds ----------
// The chips in Layers switch whole kinds of thing on and off together, any mix at once; the layers underneath
// can still be set one by one. Stars, the planets and the Moon are always there.
export type SkyKind = 'constellations' | 'starNames' | 'deepsky' | 'satellites' | 'meteors';
export const SKY_KINDS: SkyKind[] = ['constellations', 'starNames', 'deepsky', 'satellites', 'meteors'];
export const SKY_KIND_NAMES: Record<SkyKind, string> = {
  constellations: 'Constellations', starNames: 'Star names', deepsky: 'Galaxies and clusters',
  satellites: 'Satellites', meteors: 'Meteor showers',
};
/** Whether a kind is on the sky. */
export function kindOn(s: Settings, k: SkyKind): boolean {
  switch (k) {
    case 'constellations': return s.constellationLines || s.constellationNames || s.constellationBounds;
    case 'starNames': return s.starNames;
    case 'deepsky': return s.deepSky;
    case 'satellites': return s.satellites !== 'off';
    case 'meteors': return s.meteors;
  }
}
function kindLayers(s: Settings, k: SkyKind, on: boolean): Partial<Settings> {
  switch (k) {
    case 'constellations': return on ? { constellationLines: true, constellationNames: true } : { constellationLines: false, constellationNames: false, constellationBounds: false };
    case 'starNames': return { starNames: on };
    case 'deepsky': return { deepSky: on };
    case 'satellites': return { satellites: on ? (s.satellites === 'all' ? 'all' : 'bright') : 'off' };
    case 'meteors': return { meteors: on };
  }
}
/** Switch kinds on or off (one, or all of them for "Everything"). */
export function showKinds(kinds: SkyKind[], on: boolean) {
  const s = settings.value;
  settings.value = Object.assign({ ...s }, ...kinds.map((k) => kindLayers(s, k, on)));
}
// 30 Sep 2026: "Show only" (one kind at a time, then back to how it was) became any mix of kinds. One left on
// puts back what was there before it.
{
  const f = loadJSON<{ before?: unknown } | null>('focus', null);
  if (f?.before) settings.value = cleanSettings(f.before, settings.peek());
  if (f) saveJSON('focus', null);
}
export function setSetting<K extends keyof Settings>(k: K, v: Settings[K]) {
  settings.value = { ...settings.value, [k]: v };
}

// ---------- Place ----------
export const DEFAULT_PLACE: Place = { lat: 51.5074, lon: -0.1278, height: 20, name: 'London' };
/**
 * A saved place, checked. Latitude and longitude go together: without both there is no position, and so no place
 * (London instead, as for a first visit). The name, height and time zone are checked one by one: a bad name becomes
 * none (the top bar then shows the position), a bad height London's, and a bad time zone the device's own.
 */
export function cleanPlace(v: unknown): Place {
  const p = v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
  if (!within(p.lat, -90, 90) || !within(p.lon, -180, 180)) return DEFAULT_PLACE;
  const out: Place = { lat: p.lat, lon: p.lon, height: within(p.height, -500, 9000) ? p.height : DEFAULT_PLACE.height, name: typeof p.name === 'string' && p.name.trim() ? p.name.slice(0, 160) : 'My location' };
  if (isTimeZone(p.timeZone)) out.timeZone = p.timeZone;
  return out;
}
export const place = signal<Place>(cleanPlace(loadJSON<unknown>('place', null)));
export const placeSource = signal<'saved' | 'gps' | 'manual' | 'default'>(
  (() => {
    try {
      return savedText('place') ? 'saved' : 'default';
    } catch {
      return 'default';
    }
  })(),
);
/** While previewing the sky from another place (e.g. a dark site), the home place to return to. */
export const previewFrom = signal<{ home: Place; label: string; kind: 'site' | 'link' | 'relive' } | null>(null);
effect(() => {
  const p = place.value;
  if (!previewFrom.value) saveJSON('place', p);
});
/** Show the sky from somewhere else without changing home: a suggested site, or a shared link. */
export function previewPlace(p: Place, label: string, kind: 'site' | 'link' | 'relive' = 'site') {
  if (!previewFrom.peek()) previewFrom.value = { home: place.peek(), label, kind };
  else previewFrom.value = { ...previewFrom.peek()!, label, kind };
  place.value = p;
}
/** A new home place (chosen in Location): any preview, or a night being relived, ends without going back. */
export function setHome(p: Place) {
  previewFrom.value = null;
  place.value = p;
}
export function endPreview() {
  const f = previewFrom.peek();
  if (!f) return;
  place.value = f.home;
  previewFrom.value = null;
}

// ---------- Time ----------
// Simulated time = anchorSim + (real − anchorReal) × rate.
let anchorReal = Date.now();
let anchorSim = anchorReal;
export const timeRate = signal(1);
export const timeLive = signal(true);
export const timeVersion = signal(0); // bumps on jumps so views can refresh

export function simNow(): Date {
  const real = Date.now();
  return new Date(anchorSim + (real - anchorReal) * timeRate.peek());
}
export function setSimTime(d: Date, rate = timeRate.peek()) {
  anchorReal = Date.now();
  anchorSim = d.getTime();
  timeRate.value = rate;
  timeLive.value = rate === 1 && Math.abs(anchorSim - anchorReal) < 1500;
  timeVersion.value++;
}
export function setRate(rate: number) {
  setSimTime(simNow(), rate);
}
export function goLive() {
  setSimTime(new Date(), 1);
}

// ---------- View ----------
export type ViewMode = 'manual' | 'ar';
export const viewMode = signal<ViewMode>('manual');
/**
 * Pointing has stepped aside but the phone is still watched: a finger took over ('drag') or the phone
 * is lying flat ('flat'). It takes over again when the phone is aimed somewhere new or lifted.
 */
export const pointingPaused = signal<'drag' | 'flat' | null>(null);
/** The introduction film, replayed from Help. */
export const introOpen = signal(false);
/** The sky calendar's film, opened from Help. */
export const calendarFilmOpen = signal(false);
/** An update told in pages (src/ui/UpdateStory.tsx), by its release's id, opened again from the list of updates. */
export const storyOpen = signal<string | null>(null);
/** The update story on screen, whether it came by itself after an update or was opened again (for feedback's list of
 *  what was looked at, src/ui/trail.ts). */
export const storyShown = signal<string | null>(null);
/** A one-time offer is on screen (src/ui/OnceOffer.tsx): the other offers wait for it. */
export const onceOfferShown = signal(false);
/** On an iPhone, motion access needs a tap: offer it when Ahillah opens. */
export const pointingPrompt = signal(false);
export const fovDeg = signal(80);

// ---------- Selection ----------
export type Selection =
  | { kind: 'star'; index: number }
  | { kind: 'body'; id: string }
  | { kind: 'dso'; index: number }
  | { kind: 'constellation'; index: number }
  | { kind: 'satellite'; id: number }
  | { kind: 'shower'; code: string }
  | { kind: 'small'; name: string }
  | { kind: 'arabic'; index: number }
  /** A figure of the current shared sky culture (Norse, Chinese…). */
  | { kind: 'figure'; index: number }
  /** Something found in SIMBAD by tapping the sky when zoomed in. */
  | SimbadObject
  | { kind: 'manzil'; index: number };

export const selection = signal<Selection | null>(null);
/** The chosen object's card, made small (its name and where it is) to leave the sky clear; it stays small, for
 * whatever is chosen next, until opened again. */
export const infoMinimised = signal(false);
effect(() => {
  if (!selection.value && infoMinimised.value) infoMinimised.value = false;
});
/** A selection as a stable key (for remembering things about the chosen object). */
export const selKey = (sel: Selection | null) => (sel ? JSON.stringify(sel) : '');
/** A better time to see the chosen object (from Tonight): offered on its card, never jumped to unasked. */
export const bestView = signal<{ key: string; t: number } | null>(null);
// It belongs to that one opening of the card: gone once the card closes or something else is chosen.
effect(() => {
  const k = selKey(selection.value);
  if (bestView.peek() && bestView.peek()!.key !== k) bestView.value = null;
});
// A figure (or Arabian group) belongs to the culture it came from: switching cultures clears it.
let shownCulture = settings.peek().skyCulture;
effect(() => {
  const c = settings.value.skyCulture;
  if (c === shownCulture) return;
  shownCulture = c;
  const k = selection.peek()?.kind;
  if (k === 'figure' || k === 'arabic') selection.value = null;
});
/** What the centre of the view is pointing at (AR mode reticle). */
export const pointingAt = signal<{ label: string; sel: Selection } | null>(null);
/** The evening of the 29th, around sunset at home: pointing mode frames the horizon where the Moon is (src/ui/Crescent.tsx). */
export const crescentFrame = signal(false);

// ---------- Panels ----------
/** 'walkthrough' is Help's "Show me around" (src/ui/Guide.tsx): the real controls, one at a time. */
export type Panel = null | 'search' | 'tonight' | 'layers' | 'settings' | 'location' | 'time' | 'tour' | 'go' | 'hunt' | 'help' | 'learn' | 'lesson' | 'quiz' | 'nights' | 'night' | 'whatsnew' | 'islam' | 'calendar' | 'gallery' | 'walkthrough' | 'name'
  /** "How big are they?": to-scale size comparisons (src/ui/SizeCompare.tsx). */
  | 'sizes'
  /** The owner's own page: registers this device for the notes on Saudi Arabia's announcements (src/ui/OwnerPanel.tsx). */
  | 'owner'
  /** More: every other part of Ahillah, one tap away, from the dock's fifth button (src/ui/MorePanel.tsx). */
  | 'more'
  /** Tell us: feedback, from the top of Help and from More (src/ui/FeedbackPanel.tsx). */
  | 'feedback';
/** The saved night open in the 'night' panel. */
export const openNight = signal<string | null>(null);
/** Reliving a saved night: which, and the stretch of time its timeline covers (ms). */
export const reliving = signal<{ night: string; from: number; to: number } | null>(null);
/** While reliving with the phone pointing: the thing to turn towards (shown like a chosen object's guide). */
export const reliveFocus = signal<Selection | null>(null);
/** Hide every name on the sky (quiz questions). */
export const hideLabels = signal(false);
/** The photographic survey is showing (for its on-screen credit). */
export const surveyShown = signal(false);
/** Night where the user really is (the Sun below −6° now, whatever time the sky shows), so the interface dims itself. */
export const darkOut = signal(false);
/** Size of the names drawn on the sky, following the reader's text size (1 = as designed). */
export const labelScale = signal(1);
/** Draw the centre circle in manual mode too (Sky Hunt). */
export const huntReticle = signal(false);
/** Lining up with the sky (src/live/align.ts): the circle in the middle stands out, as in Sky Hunt. */
export const alignReticle = signal(false);
export const panel = signal<Panel>(null);

export const toast = signal<{ text: string; id: number; action?: { label: string; run: () => void } } | null>(null);

/** The sky in another kind of light (an id from tex/bands/manifest.json), or null for visible light. */
export const skyBand = signal<string | null>(null);
let toastId = 0;
/** A short message; with an action (such as Undo), it stays a little longer and carries a button. */
export function showToast(text: string, ms = 3200, action?: { label: string; run: () => void }) {
  const id = ++toastId;
  toast.value = { text, id, action };
  setTimeout(() => hideToast(id), ms);
  return id;
}
/** Take down a toast early, if it is still the one showing. */
export function hideToast(id: number) {
  if (toast.peek()?.id === id) toast.value = null;
}
